/**
 * Utility to track user information for lead verification
 * Includes advanced fingerprinting (Canvas, WebGL) to detect duplicate devices
 */

// Simple hash function (djb2 variant) to create short identifiers
const generateHash = (str) => {
    let hash = 0;
    if (str.length === 0) return hash;
    for (let i = 0; i < str.length; i++) {
        const char = str.charCodeAt(i);
        hash = ((hash << 5) - hash) + char;
        hash = hash & hash; // Convert to 32bit integer
    }
    return Math.abs(hash).toString(16);
};

// Fetch public IP address
export const getUserIp = async () => {
    try {
        const response = await fetch('https://api.ipify.org?format=json');
        if (!response.ok) throw new Error('IP fetch failed');
        const data = await response.json();
        return data.ip;
    } catch (error) {
        console.error('Error fetching IP:', error);
        return 'Unknown IP';
    }
};

// Canvas Fingerprinting: Renders hidden text/shapes and hashes the pixel data
// Unique to GPU + Browser + OS combination
export const getCanvasFingerprint = () => {
    try {
        const canvas = document.createElement('canvas');
        const ctx = canvas.getContext('2d');
        canvas.width = 200;
        canvas.height = 50;

        // Text with different fonts and styles
        ctx.textBaseline = 'top';
        ctx.font = '14px "Arial"';
        ctx.textBaseline = 'alphabetic';
        ctx.fillStyle = '#f60';
        ctx.fillRect(125, 1, 62, 20);
        ctx.fillStyle = '#069';
        ctx.fillText('OmkarAuth', 2, 15);
        ctx.fillStyle = 'rgba(102, 204, 0, 0.7)';
        ctx.fillText('OmkarAuth', 4, 17);

        return generateHash(canvas.toDataURL());
    } catch (e) {
        return 'canvas-error';
    }
};

// WebGL Fingerprinting: Extracts GPU Vendor and Renderer info
// Very hard to fake unless using specialized anti-detect browsers
export const getWebGLFingerprint = () => {
    try {
        const canvas = document.createElement('canvas');
        const gl = canvas.getContext('webgl') || canvas.getContext('experimental-webgl');
        if (!gl) return { vendor: 'unsupported', renderer: 'unsupported' };

        const debugInfo = gl.getExtension('WEBGL_debug_renderer_info');
        if (!debugInfo) return { vendor: 'unknown', renderer: 'unknown' };

        return {
            vendor: gl.getParameter(debugInfo.UNMASKED_VENDOR_WEBGL),
            renderer: gl.getParameter(debugInfo.UNMASKED_RENDERER_WEBGL),
        };
    } catch (e) {
        return { vendor: 'error', renderer: 'error' };
    }
};

// Get basic browser fingerprint details
export const getBrowserFingerprint = () => {
    try {
        const nav = window.navigator;
        const screen = window.screen;

        return {
            userAgent: nav.userAgent,
            language: nav.language,
            platform: nav.platform,
            screenResolution: `${screen.width}x${screen.height}`,
            colorDepth: screen.colorDepth,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            hardwareConcurrency: nav.hardwareConcurrency || 'unknown',
            deviceMemory: nav.deviceMemory || 'unknown',
            touchSupport: 'ontouchstart' in window || nav.maxTouchPoints > 0,
            cookieEnabled: nav.cookieEnabled,
        };
    } catch (error) {
        console.error('Error getting fingerprint:', error);
        return { error: 'Failed to capture fingerprint' };
    }
};

// Combine all info
export const getUserTrackingInfo = async () => {
    const ip = await getUserIp();
    const basic = getBrowserFingerprint();
    const canvasHash = getCanvasFingerprint();
    const webGL = getWebGLFingerprint();

    // Create a composite Visitor ID from stable hardware traits
    // (Screen Res + Timezone + Platform + Canvas Hash + WebGL Renderer)
    const rawId = `${basic.screenResolution}-${basic.timezone}-${basic.platform}-${canvasHash}-${webGL.renderer}`;
    const visitorId = generateHash(rawId);

    return {
        ip,
        visitorId,
        canvasHash,
        webGL,
        ...basic
    };
};
